import { z } from 'zod';
/**
 * Play (ADR-022): the third pillar. You log what you did from a catalogue,
 * and nothing about how you felt — recording an afternoon is ordinary
 * personal data, recording a mood is a different promise and is not made.
 */
/** What a thing gives you. A week is read by which of these it touched. */
export const playQualitySchema = z.enum([
    'outdoors',
    'together',
    'moving',
    'making',
    'still',
    'playful',
    'learning',
]);
export const PLAY_QUALITIES = playQualitySchema.options;
/** How each quality reads on screen, in the second person. */
export const PLAY_QUALITY_LABELS = {
    outdoors: 'Outdoors',
    together: 'With people',
    moving: 'Moving',
    making: 'Making',
    still: 'Rest',
    playful: 'Playful',
    learning: 'Learning',
};
export const playCategorySchema = z.enum([
    'outdoors',
    'water',
    'games',
    'making',
    'music',
    'people',
    'quiet',
    'outing',
    'silly',
]);
export const PLAY_CATEGORY_LABELS = {
    outdoors: 'Outdoors',
    water: 'On the water',
    games: 'Games',
    making: 'Making things',
    music: 'Music',
    people: 'People',
    quiet: 'Quiet',
    outing: 'Out and about',
    silly: 'Silly',
};
export const playOptionSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    emoji: z.string().nullable(),
    category: playCategorySchema,
    qualities: z.array(playQualitySchema),
    typicalMin: z.number().int(),
    blurb: z.string().nullable(),
});
export const playLogSchema = z.object({
    id: z.string().uuid(),
    memberId: z.string().uuid(),
    date: z.string(),
    playOptionId: z.string().uuid().nullable(),
    /** What it was called, from the catalogue or in their own words. */
    title: z.string(),
    emoji: z.string().nullable(),
    qualities: z.array(playQualitySchema),
    minutes: z.number().int().nullable(),
    withOthers: z.boolean(),
    note: z.string().nullable(),
});
export const logPlaySchema = z
    .object({
    memberId: z.string().uuid(),
    date: z.string().date(),
    playOptionId: z.string().uuid().nullish(),
    /** Something the catalogue does not have, in their own words. */
    freeText: z.string().min(1).max(200).nullish(),
    minutes: z.number().int().min(1).max(1440).nullish(),
    withOthers: z.boolean().default(false),
    note: z.string().max(300).nullish(),
})
    .refine((v) => Boolean(v.playOptionId) || Boolean(v.freeText), {
    message: 'Say what you did: pick something, or write it in',
});
export const playWeekQuerySchema = z.object({
    memberId: z.string().uuid(),
    from: z.string().date(),
    to: z.string().date(),
});
/**
 * A week's variety: which qualities it touched and how often. Deliberately
 * not a score — a single number implies seven out of seven is the goal, and
 * a week spent entirely in a workshop is not a failure (ADR-022).
 */
export const playWeekSchema = z.object({
    from: z.string(),
    to: z.string(),
    entries: z.number().int(),
    minutes: z.number().int(),
    days: z.number().int(),
    touched: z.record(playQualitySchema, z.number().int()),
});
/**
 * Play planned in the week: an idea for the day, not an appointment
 * (ADR-032). A day and never a time; planned or done, never missed.
 */
export const playSlotSchema = z.object({
    id: z.string().uuid(),
    date: z.string(),
    memberId: z.string().uuid(),
    memberName: z.string(),
    playOptionId: z.string().uuid().nullable(),
    /** From the catalogue, or in their own words. */
    title: z.string(),
    emoji: z.string().nullable(),
    minutes: z.number().int().nullable(),
    note: z.string().nullable(),
    status: z.enum(['planned', 'done']),
    playLogId: z.string().uuid().nullable(),
    /** Planned for another claimed adult: offered until they answer (ADR-016). */
    state: z.enum(['offered', 'accepted', 'declined']),
});
export const planPlaySchema = z
    .object({
    date: z.string().date(),
    /** Everybody it is for: one entry each, so each can answer for themselves. */
    memberIds: z.array(z.string().uuid()).min(1).max(20),
    playOptionId: z.string().uuid().nullish(),
    freeText: z.string().trim().min(1).max(200).nullish(),
    minutes: z.number().int().min(1).max(1440).nullish(),
    note: z.string().max(300).nullish(),
})
    .refine((v) => Boolean(v.playOptionId) !== Boolean(v.freeText), {
    message: 'Say what it is: pick something, or write it in',
});
//# sourceMappingURL=play.js.map